import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Expand, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent } from '../ui/dialog';

const HomeGalleryCard = ({ item }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);

  // Safety check
  if (!item) {
    return null;
  }

  const hasImages = item.images && item.images.length > 0;
  const hasMultipleImages = item.images && item.images.length > 1;

  const nextImage = (e) => {
    e.stopPropagation();
    if (hasImages) {
      setCurrentImageIndex((prev) => (prev + 1) % item.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (hasImages) {
      setCurrentImageIndex((prev) => (prev - 1 + item.images.length) % item.images.length);
    }
  };

  const openLightbox = (index) => {
    setLightboxImageIndex(index);
    setIsLightboxOpen(true);
  };

  const nextLightboxImage = () => {
    if (hasImages) {
      setLightboxImageIndex((prev) => (prev + 1) % item.images.length);
    }
  };

  const prevLightboxImage = () => {
    if (hasImages) {
      setLightboxImageIndex((prev) => (prev - 1 + item.images.length) % item.images.length);
    }
  };

  return (
    <>
      <Card className="bg-slate-800 border-slate-700 overflow-hidden hover:border-gray-400/50 transition-all duration-300">
        {hasImages ? (
          <div className="relative h-48 bg-slate-900 group">
            <img 
              src={item.images[currentImageIndex]} 
              alt={`${item.title} ${currentImageIndex + 1}`}
              className="w-full h-full object-cover cursor-pointer"
              onClick={() => openLightbox(currentImageIndex)}
            />
            
            {/* Expand icon overlay */}
            <div 
              className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              onClick={() => openLightbox(currentImageIndex)}
            >
              <Expand className="w-10 h-10 text-white" />
            </div>
            
            {/* Navigation arrows */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all z-10 opacity-0 group-hover:opacity-100"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all z-10 opacity-0 group-hover:opacity-100"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Image counter */}
            {hasMultipleImages && (
              <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                {currentImageIndex + 1} / {item.images.length}
              </div>
            )}
          </div>
        ) : (
          <div className="h-48 bg-slate-900 flex items-center justify-center">
            <span className="text-gray-500">No image available</span>
          </div>
        )}
        <CardHeader>
          <CardTitle className="text-white">{item.title}</CardTitle>
          {item.description && (
            <CardDescription className="text-gray-400">
              {item.description}
            </CardDescription>
          )}
        </CardHeader>
      </Card>

      {/* Lightbox Modal */}
      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-transparent border-none">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button */}
            <button
              onClick={() => setIsLightboxOpen(false)}
              className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all z-50"
              aria-label="Close lightbox"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Main image */}
            {hasImages && (
              <div className="relative w-full h-full flex items-center justify-center">
                <img
                  src={item.images[lightboxImageIndex]}
                  alt={`${item.title} ${lightboxImageIndex + 1}`}
                  className="max-w-full max-h-[85vh] object-contain"
                />
              </div>
            )}

            {/* Navigation */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevLightboxImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all z-50"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={nextLightboxImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all z-50"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}

            {/* Counter */}
            {hasMultipleImages && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm z-50">
                {lightboxImageIndex + 1} / {item.images.length}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HomeGalleryCard;
